import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, Lock } from 'lucide-react';
import type { CodeLabel, ImpactSummary, StakeholderList, StakeholderSummary } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useParameterOptions } from '../../lib/parameters';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';

export function fmtNumber(n: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n);
}

/** Engagement levels are a pipeline: later steps read warmer. */
export function engagementTone(code: string | null | undefined): 'neutral' | 'brand' | 'warn' | 'danger' | 'info' {
  switch (code) {
    case 'high':
      return 'brand';
    case 'medium':
      return 'info';
    case 'low':
      return 'warn';
    default:
      return 'neutral';
  }
}

export function importanceTone(code: string | null | undefined): 'neutral' | 'brand' | 'warn' | 'danger' | 'info' {
  return code === 'high' ? 'danger' : code === 'medium' ? 'warn' : 'neutral';
}

export function Label({ value }: { value: CodeLabel | null }) {
  const { i18n } = useTranslation();
  return <>{value ? pick(i18n.language, value.name_en, value.name_ar) : '—'}</>;
}

/** Square logo box (MRLCO: letter-boxed `object-contain`, building icon fallback). */
export function Logo({ url, name, size = 48, className }: { url: string | null; name: string; size?: number; className?: string }) {
  return (
    <div className={cn('flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200', className)} style={{ width: size, height: size }}>
      {url ? <img src={url} alt="" loading="lazy" decoding="async" className="max-h-full max-w-full object-contain" /> : name ? <span className="text-lg font-semibold">{name.slice(0, 1)}</span> : <Building2 size={size / 2} />}
    </div>
  );
}

/** Impact roll-up pills: `70,000 SAR`, `Avg 45% · n=2`, restricted → `… Unit` with a lock. */
export function ImpactChips({ impacts, max = 4, className }: { impacts: ImpactSummary[]; max?: number; className?: string }) {
  const { t, i18n } = useTranslation();
  if (impacts.length === 0) return null;
  const shown = impacts.slice(0, max);
  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {shown.map((i) => (
        <span key={i.unit_code} className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs tabular-nums', i.is_total_value_component ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-200' : 'border-neutral-200 bg-neutral-50 text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300')} title={i.aggregation === 'average' ? t('stakeholders.impacts.averageOf', { count: i.sample_count }) : undefined}>
          {i.total === null ? <><Lock size={10} /> …</> : <>{i.aggregation === 'average' && <span className="text-[10px] uppercase text-neutral-500">{t('stakeholders.impacts.avg')}</span>}{fmtNumber(i.total, i18n.language)}</>}
          <span className="text-neutral-500">{pick(i18n.language, i.unit_name_en, i.unit_name_ar)}</span>
          {i.aggregation === 'average' && i.total !== null && <span className="text-[10px] text-neutral-400">· n={i.sample_count}</span>}
        </span>
      ))}
      {impacts.length > max && <Badge>+{impacts.length - max}</Badge>}
    </div>
  );
}

export function useStakeholderLookups() {
  const entityTypes = useParameterOptions('entity_type');
  const engagement = useParameterOptions('engagement_level');
  const importance = useParameterOptions('importance');
  return { entityTypes, engagement, importance };
}

export function useStakeholderSummary(enabled = true) {
  return useQuery({ queryKey: ['stakeholders', 'summary'], queryFn: () => api.get<StakeholderSummary>('/api/stakeholders/summary'), enabled, staleTime: 30_000 });
}

/** Options for parent / stakeholder pickers (first 200 by name). */
export function useStakeholderOptions(enabled = true, excludeId?: string) {
  const { i18n } = useTranslation();
  const q = useQuery({ queryKey: ['stakeholders', 'options'], queryFn: () => api.get<StakeholderList>('/api/stakeholders?page_size=200&sort=name_en'), enabled, staleTime: 60_000 });
  const options = useMemo(() => (q.data?.items ?? []).filter((s) => s.id !== excludeId).map((s) => ({ value: s.id, label: pick(i18n.language, s.name_en, s.name_ar) })), [q.data, i18n.language, excludeId]);
  return { options, loading: q.isLoading };
}
